import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  LayoutDashboard,     // Dashboard (single)
  Briefcase,           // Operations
  TrendingUp,          // Sales
  ShoppingBag,         // Purchases
  Calculator,          // Accounting
  Wallet,              // Expenses
  Banknote,            // Banking
  Package,             // Inventory
  UsersRound,          // HR & Payroll
  BarChart4,           // Reports
  Building2,           // Assets & Company
  Factory as FactoryIcon,  // Factory
  Settings as SettingsIcon, // Settings
  ReceiptText,         // legacy fallback
  Store,               // legacy fallback
  ShieldCheck,         // legacy fallback
  Folder,              // ultimate fallback
  Lock,                // iter595 — feature-flag locked indicator
  Receipt,             // iter711 — Tax & Compliance group
  Cpu,                 // iter711 — POS & Hardware group
  Database,            // iter711 — System & Data group
  Activity,            // iter711 — Audit & Health group
  IdCard,              // iter711 — Company & Identity group
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { navLabelToKey } from '@/lib/i18n';
import { useTaxConfig } from '@/hooks/useTaxConfig';
import { NAV_DESCRIPTIONS, hasPermission } from '@/config/navigationConfig';
import { useFeatureFlagsStore } from '@/stores/featureFlagsStore';
import { NewBadge } from '@/components/NewBadge';

// Group-level icons. Updated for the iter893 sidebar re-org
// (Dashboard → Operations and POS → Sales and Customers →
//  Purchases and Suppliers → Finance and Accounting → People →
//  Setup and Admin). Old labels kept as fallbacks so any cached
// state with the legacy keys still finds a glyph.
const GROUP_ICONS = {
  // iter893 six-section taxonomy
  'Dashboard': LayoutDashboard,
  'Operations and POS': Briefcase,
  'Sales and Customers': TrendingUp,
  'Purchases and Suppliers': ShoppingBag,
  'Finance and Accounting': Calculator,
  'People': UsersRound,
  'Setup and Admin': SettingsIcon,
  // Previous (iter344/iter711) taxonomy — fallback glyphs kept so any
  // cached localStorage state or legacy hard-coded reference still
  // renders an icon instead of the ultimate `Folder` fallback.
  'Operations': Briefcase,
  'Sales': TrendingUp,
  'Purchases': ShoppingBag,
  'Accounting': Calculator,
  'Expenses': Wallet,
  'Banking': Banknote,
  'Inventory': Package,
  'HR & Payroll': UsersRound,
  'Reports': BarChart4,
  'Assets & Company': Building2,
  'Factory': FactoryIcon,
  'Settings': SettingsIcon,
  'Company & Identity': IdCard,
  'Tax & Compliance': Receipt,
  'POS & Hardware': Cpu,
  'System & Data': Database,
  'Audit & Health': Activity,
  'Sales & Revenue': TrendingUp,
  'Expenses & Payables': ReceiptText,
  'HR & Workforce': UsersRound,
  'POS & Inventory': Store,
  'Reports & Analytics': BarChart4,
  'Admin': ShieldCheck,
};

// iter895 — Subtle tinted background bar per parent section so
// operators recognise the group at a glance without reading labels.
// Each tone is a soft light + dark theme pair; hover/active tints are
// applied via ring + primary color inside the header button. Keep
// saturation LOW so the sidebar stays scannable — these are
// wayfinding cues, not brand accents.
const GROUP_TONES = {
  'Operations and POS':       { bg: 'bg-indigo-50/60 dark:bg-indigo-950/25',   rail: 'bg-indigo-400/60' },
  'Sales and Customers':      { bg: 'bg-rose-50/60 dark:bg-rose-950/25',       rail: 'bg-rose-400/60' },
  'Purchases and Suppliers':  { bg: 'bg-amber-50/60 dark:bg-amber-950/25',     rail: 'bg-amber-400/60' },
  'Finance and Accounting':   { bg: 'bg-emerald-50/60 dark:bg-emerald-950/25', rail: 'bg-emerald-400/60' },
  'People':                   { bg: 'bg-violet-50/60 dark:bg-violet-950/25',   rail: 'bg-violet-400/60' },
  'Setup and Admin':          { bg: 'bg-slate-100/70 dark:bg-slate-800/30',    rail: 'bg-slate-400/60' },
};

const STORAGE_KEY = 'smarto_nav_open_state_v1';
// iter930 — Nested sub-folder open state (keyed by "parent::child").
// Kept in a separate localStorage bucket so a schema change to sub-
// folders can be reset without wiping the parent-level open state.
const SUBGROUP_STORAGE_KEY = 'smarto_nav_subgroup_open_state_v1';

function loadOpenState() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function saveOpenState(label, isOpen) {
  try {
    const s = loadOpenState();
    s[label] = isOpen;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch { /* ignore */ }
}

function loadSubgroupState() {
  try {
    return JSON.parse(localStorage.getItem(SUBGROUP_STORAGE_KEY) || '{}');
  } catch { return {}; }
}

function saveSubgroupState(key, isOpen) {
  try {
    const s = loadSubgroupState();
    s[key] = isOpen;
    localStorage.setItem(SUBGROUP_STORAGE_KEY, JSON.stringify(s));
  } catch { /* ignore */ }
}

// Lightweight subsequence fuzzy match: every char of `query` must appear
// in `target` in order (case-insensitive). Also returns true on substring.
// No external dependency — handles typos like "invoic" → "Invoices" and
// abbreviations like "prof inv" → "Proforma Invoices".
export function fuzzyMatch(query, target) {
  if (!query) return true;
  const q = query.toLowerCase().trim();
  const t = (target || '').toLowerCase();
  if (!q) return true;
  if (t.includes(q)) return true;
  let i = 0;
  for (let j = 0; j < t.length && i < q.length; j++) {
    if (t[j] === q[i]) i++;
  }
  return i === q.length;
}

// iter931 — Density token map. Two tiers (comfortable = iter930
// default, compact = ~30% tighter). Everything sidebar-related pulls
// from here so a future 'ultra' tier is one line to add.
const DENSITY = {
  comfortable: {
    groupHeader: 'py-[9px] text-[11px]',
    item:        'py-[8px] text-[13px]',
    subHeader:   'py-[6px] text-[10.5px]',
    spacing:     'space-y-[3px]',
    iconSize:    15,
    subChevron:  11,
    groupIcon:   14,
  },
  compact: {
    groupHeader: 'py-[6px] text-[10.5px]',
    item:        'py-[5px] text-[12px]',
    subHeader:   'py-[4px] text-[10px]',
    spacing:     'space-y-[1px]',
    iconSize:    14,
    subChevron:  10,
    groupIcon:   13,
  },
};

// iter930 — Extract a single item's row so both the flat list AND
// each sub-folder can reuse identical styling + testids. Keeps the
// component tree readable now that the sidebar renders items in 3
// contexts: top-level (flat), inside a sub-folder, and single-item
// group.
// iter931 — Accepts a `density` prop to switch between comfortable
// and compact spacing tokens without duplicating the JSX.
function NavItemLink({ item, currentPath, searchSelectedPath, onNavigate, t, flags, indent = 'pl-6', density = 'comfortable' }) {
  const Icon = item.icon;
  const isActive = currentPath === item.path;
  const isKbSelected = !!searchSelectedPath && searchSelectedPath === item.path;
  const isLocked = !!(item.featureFlag && !flags[item.featureFlag]);
  const D = DENSITY[density] || DENSITY.comfortable;
  return (
    <Link
      to={item.path}
      onClick={() => onNavigate?.(item.path)}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`sidebar-nav-item flex items-center gap-2.5 ${indent} pr-3 ${D.item} rounded-[10px] transition-all duration-150 relative hover:translate-x-[1px] ${
        isActive
          ? 'active bg-primary text-white font-semibold shadow-[0_2px_8px_rgba(0,0,0,0.14)]'
          : isKbSelected
            ? 'bg-primary/10 text-foreground font-semibold ring-1 ring-primary/30'
            : 'text-muted-foreground hover:bg-white/70 dark:hover:bg-stone-700/40 hover:text-foreground dark:hover:text-stone-200 font-medium'
      } ${isLocked ? 'opacity-70' : ''}`}
      title={isLocked ? 'Locked — open this page to unlock via the admin code' : undefined}
    >
      {isActive && <span className="absolute left-2 top-1.5 bottom-1.5 w-[3px] rounded-full bg-white" />}
      <Icon size={D.iconSize} strokeWidth={isActive ? 2.4 : 1.6} className={isActive ? 'text-white' : 'opacity-60'} />
      <span className="truncate">{t(navLabelToKey[item.label] || item.label)}</span>
      {isLocked && (
        <Lock
          size={11}
          strokeWidth={2.2}
          className={`ml-auto flex-shrink-0 ${isActive ? 'text-white/80' : 'text-amber-600 dark:text-amber-400'}`}
          data-testid={`nav-lock-${item.path.replace(/\//g, '-')}`}
        />
      )}
      {item.newSince && !isLocked && (
        <NewBadge
          since={item.newSince}
          featureId={`nav-${item.path}`}
          size="xs"
          dismissable={false}
          className="ml-auto flex-shrink-0"
        />
      )}
    </Link>
  );
}

// iter930 — SubFolder: a nested collapsible inside a top-level group.
// Its open state is persisted independently in `smarto_nav_subgroup_open_state_v1`
// keyed by `${parent}::${sub.label}` so state survives page reloads.
// When any item inside is active, the sub-folder auto-opens on mount.
function SubFolder({ parentLabel, sub, permittedItems, currentPath, searchSelectedPath, onNavigate, t, flags, isSearching, density = 'comfortable' }) {
  const key = `${parentLabel}::${sub.label}`;
  const hasActive = permittedItems.some((i) => i.path === currentPath);
  const saved = loadSubgroupState();
  const initialOpen = isSearching || hasActive || saved[key] === true;
  const [open, setOpen] = useState(initialOpen);
  useEffect(() => {
    if (hasActive && !open) setOpen(true);
  }, [hasActive, open]);
  const effectiveOpen = isSearching ? true : open;
  const toggle = () => {
    const next = !open;
    setOpen(next);
    saveSubgroupState(key, next);
  };
  if (permittedItems.length === 0) return null;
  const D = DENSITY[density] || DENSITY.comfortable;
  const testid = `nav-subgroup-${parentLabel.toLowerCase().replace(/\s+/g, '-')}-${sub.label.toLowerCase().replace(/\s+&\s+/g, '-').replace(/\s+/g, '-')}`;
  return (
    <div className="mt-0.5" data-testid={testid}>
      <button
        type="button"
        onClick={toggle}
        data-testid={`${testid}-toggle`}
        className={`w-full flex items-center gap-1.5 pl-3 pr-2 ${D.subHeader} rounded-[8px] font-semibold uppercase tracking-[0.05em] transition-all ${
          hasActive
            ? 'text-primary/90 bg-primary/5'
            : 'text-muted-foreground/70 hover:text-foreground hover:bg-white/50 dark:hover:bg-stone-700/25'
        }`}
      >
        <ChevronDown
          size={D.subChevron}
          className={`transition-transform duration-200 ${effectiveOpen ? 'rotate-0' : '-rotate-90'} opacity-70`}
        />
        <span className="flex-1 text-left truncate">{sub.label}</span>
      </button>
      <div className={`grid transition-[grid-template-rows] duration-200 ease-out ${effectiveOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
        <div className="overflow-hidden">
          <div className={`${D.spacing} pt-0.5 pb-1 pl-1`}>
            {permittedItems.map((item) => (
              <NavItemLink
                key={item.path}
                item={item}
                currentPath={currentPath}
                searchSelectedPath={searchSelectedPath}
                onNavigate={onNavigate}
                t={t}
                flags={flags}
                indent="pl-7"
                density={density}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function NavGroup({ group, userRole, userPerms, allowedModules, currentPath, onNavigate, t, isSuperAdmin, searchQuery = '', searchSelectedPath = null, openGroup, onOpenGroupChange, colorsEnabled, density = 'comfortable' }) {
  // iter595 — Items can declare a `featureFlag`. We don't HIDE locked
  // items (admins still need to reach the page to flip the toggle on);
  // instead the renderer below decorates them with a 🔒 lock badge.
  const flags = useFeatureFlagsStore(s => s.flags);
  // iter609 — Country-aware visibility. UK tenants don't need to see
  // ZATCA, Saudi tenants don't need to see HMRC. Items can declare a
  // `country: ['sa']` or `country: ['uk', 'gb']` array. Items without
  // a country list show everywhere (default). Admins and super admins
  // bypass the gate so they can still access the page directly via URL.
  //
  // iter876 — Global multi-country: use the CURRENT USER's branch country
  // (not tenant default) so a tenant operating in KSA + UAE + IN sees the
  // right sub-menus for the branch they're logged into.
  const userBranchId = useAuthStore(s => s.user?.branch_id) || null;
  const { taxConfig } = useTaxConfig(userBranchId);
  const tenantCountry = String(taxConfig?.country_code || '').toLowerCase();
  const matchesCountry = (item) => {
    if (!Array.isArray(item.country) || item.country.length === 0) return true;
    if (!tenantCountry) return true; // unknown country → don't hide anything
    return item.country.map((c) => String(c).toLowerCase()).includes(tenantCountry);
  };
  const permittedItems = (group.items || []).filter(item => {
    if (item.superAdmin && !isSuperAdmin) return false;
    if (!matchesCountry(item)) return false;
    if (userRole === 'admin') return true;
    if (item.roles && !item.roles.includes(userRole || 'operator')) return false;
    if (item.perm) {
      return hasPermission(userPerms, item.perm, userRole, allowedModules);
    }
    return true;
  });

  // iter930 — Filter each declared sub-folder through the same
  // permission gate. Empty sub-folders are dropped entirely, so a
  // manager who can only see "Bank Statements" inside a 12-item
  // "Banking" sub-folder still sees a compact 1-item folder — but a
  // sub-folder with zero permitted items disappears from the DOM.
  const filterItems = (items) => (items || []).filter((item) => {
    if (item.superAdmin && !isSuperAdmin) return false;
    if (!matchesCountry(item)) return false;
    if (userRole === 'admin') return true;
    if (item.roles && !item.roles.includes(userRole || 'operator')) return false;
    if (item.perm) return hasPermission(userPerms, item.perm, userRole, allowedModules);
    return true;
  });
  const permittedSubgroups = (group.subgroups || [])
    .filter((sub) => !sub.hidden)  // iter — `hidden:true` keeps the group in config but off the sidebar
    .map((sub) => ({ ...sub, permittedItems: filterItems(sub.items) }))
    .filter((sub) => sub.permittedItems.length > 0);

  // When the user types in the sidebar search, narrow items to those
  // matching the query. If the GROUP label itself matches, keep all items
  // (so typing "sales" reveals every Sales item, not just the ones
  // containing "sales" in their name).
  const groupLabelMatches = searchQuery && fuzzyMatch(searchQuery, group.label);
  const filteredItems = searchQuery && !groupLabelMatches
    ? permittedItems.filter(i => fuzzyMatch(searchQuery, i.label))
    : permittedItems;

  // iter930 — Apply the same fuzzy filter to sub-folder items so a
  // search for "reconc" still lifts matching items out of nested
  // folders. Sub-folder headers auto-expand when searching so hits
  // are always visible without extra clicks.
  const filteredSubgroups = searchQuery && !groupLabelMatches
    ? permittedSubgroups
        .map((sub) => ({ ...sub, permittedItems: sub.permittedItems.filter((i) => fuzzyMatch(searchQuery, i.label)) }))
        .filter((sub) => sub.permittedItems.length > 0)
    : permittedSubgroups;

  const subgroupItemCount = filteredSubgroups.reduce((n, s) => n + s.permittedItems.length, 0);
  const totalItemCount = filteredItems.length + subgroupItemCount;

  const hasActive = filteredItems.some(i => i.path === currentPath)
    || filteredSubgroups.some((s) => s.permittedItems.some((i) => i.path === currentPath));
  const saved = loadOpenState();
  const savedValue = saved[group.label];
  // iter893 — Single-accordion mode. When the parent supplies
  // `openGroup` / `onOpenGroupChange` we're in controlled mode: only
  // one group can be open at a time and clicking a header collapses
  // all siblings. Un-controlled mode (no props) falls back to the
  // legacy per-group localStorage state so any external caller that
  // still renders NavGroup standalone keeps working.
  const isControlled = typeof openGroup === 'string' || openGroup === null;
  const isSearching = !!searchQuery;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(hasActive || savedValue === true);
  const controlledOpen = isControlled ? openGroup === group.label : uncontrolledOpen;
  // Show open when: searching (all groups expanded), OR the group is
  // the active one (contains current page), OR user has explicitly
  // opened it.
  const effectiveOpen = isSearching ? true : (controlledOpen || (hasActive && openGroup === undefined));

  // If user navigates to an item inside a collapsed group, open it
  // automatically. We intentionally only depend on `hasActive` — the
  // other identifiers stay stable across the same render pass, and
  // re-firing this on every re-render would fight the manual toggle
  // below.
  useEffect(() => {
    if (!hasActive) return;
    if (isControlled) {
      if (openGroup !== group.label) onOpenGroupChange?.(group.label);
    } else if (!uncontrolledOpen) {
      setUncontrolledOpen(true);
    }
  }, [hasActive]);

  // iter893 — Auto-scroll: when a section expands, center it inside
  // the scrollable sidebar so the operator sees the full list without
  // manually scrolling. Debounced via rAF so it runs after the CSS
  // grid-rows animation kicks in. Skipped while searching (all groups
  // are open, scrolling one into view would jump the sidebar around).
  const groupRef = useRef(null);
  useEffect(() => {
    if (!effectiveOpen || isSearching) return;
    const el = groupRef.current;
    if (!el || typeof el.scrollIntoView !== 'function') return;
    const id = requestAnimationFrame(() => {
      try {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      } catch { /* older browsers */ el.scrollIntoView(); }
    });
    return () => cancelAnimationFrame(id);
  }, [effectiveOpen, isSearching]);

  // iter614 — Sidebar simplification: when a group has many items
  // (Reports, HR, Settings have 12-24 each) only show the top 8 by
  // default; the rest live behind a "Show all (+N more)" toggle. The
  // currently-active page is always visible regardless of the cutoff
  // so the user never loses their place. Disabled while searching so
  // every match stays reachable.
  const COMPACT_LIMIT = 8;
  const [showAll, setShowAll] = useState(() => {
    try { return localStorage.getItem(`smarto_nav_showall_v1::${group.label}`) === '1'; }
    catch { return false; }
  });
  const toggleShowAll = () => {
    const next = !showAll;
    setShowAll(next);
    try { localStorage.setItem(`smarto_nav_showall_v1::${group.label}`, next ? '1' : '0'); } catch { /* */ }
  };
  const needsCompact = !isSearching && !showAll && filteredItems.length > COMPACT_LIMIT;
  let visibleItems = filteredItems;
  if (needsCompact) {
    const primary = filteredItems.slice(0, COMPACT_LIMIT);
    // Ensure the active item is always rendered so the user can still
    // see where they are.
    const activeIdx = filteredItems.findIndex(i => i.path === currentPath);
    if (activeIdx >= COMPACT_LIMIT) {
      visibleItems = [...primary, filteredItems[activeIdx]];
    } else {
      visibleItems = primary;
    }
  }
  const hiddenCount = filteredItems.length - visibleItems.length;

  const toggle = () => {
    if (isControlled) {
      // Single-accordion: toggle open or collapse (null closes all).
      onOpenGroupChange?.(openGroup === group.label ? null : group.label);
    } else {
      const next = !uncontrolledOpen;
      setUncontrolledOpen(next);
      saveOpenState(group.label, next);
    }
  };

  if (filteredItems.length === 0 && filteredSubgroups.length === 0) return null;

  const GroupIcon = GROUP_ICONS[group.label] || Folder;
  const D = DENSITY[density] || DENSITY.comfortable;
  // iter896 — Respect the per-device "sidebar section colors"
  // preference. Prefers explicit prop from parent (DashboardLayout
  // owns the state so the toggle re-renders instantly); falls back to
  // localStorage for standalone callers.
  const _prefsColorsOn = (colorsEnabled !== undefined) ? colorsEnabled : (() => {
    if (typeof window === 'undefined') return true;
    try { return localStorage.getItem('ui_sidebar_section_colors') !== '0'; }
    catch { return true; }
  })();
  const tone = _prefsColorsOn ? GROUP_TONES[group.label] : null; // iter895 — may be undefined for legacy groups.

  // Single-item groups (like Dashboard) render as a flat top-level link
  // — no chevron, no expand state, no group header. Cleaner than wrapping
  // a one-item collapsible just for visual consistency.
  if (group.singleItem && filteredItems.length === 1) {
    const item = filteredItems[0];
    const Icon = item.icon || GroupIcon;
    const isActive = currentPath === item.path;
    const isKbSelected = !!searchSelectedPath && searchSelectedPath === item.path;
    return (
      <div className="mb-1">
        <Link
          to={item.path}
          onClick={() => onNavigate?.(item.path)}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          className={`sidebar-nav-item flex items-center gap-2.5 mx-1.5 px-3 ${D.item} rounded-[10px] transition-all duration-150 relative ${
            isActive
              ? 'active bg-primary text-white font-semibold shadow-[0_2px_6px_rgba(0,0,0,0.12)]'
              : isKbSelected
                ? 'bg-primary/10 text-foreground font-semibold ring-1 ring-primary/30'
                : 'text-foreground/80 hover:bg-white/60 dark:hover:bg-stone-700/30 hover:text-foreground font-medium'
          }`}
        >
          {isActive && <span className="absolute left-2 top-1.5 bottom-1.5 w-[3px] rounded-full bg-white" />}
          <Icon size={D.iconSize} strokeWidth={isActive ? 2.4 : 1.7} className={isActive ? 'text-white' : 'opacity-70'} />
          <span className="truncate">{t(navLabelToKey[item.label] || item.label)}</span>
        </Link>
      </div>
    );
  }

  return (
    <div ref={groupRef} className={`mb-1 rounded-xl mx-1 my-0.5 transition-colors ${tone?.bg || ''}`}>
      <button
        onClick={toggle}
        data-testid={`nav-group-${group.label.toLowerCase().replace(/\s&\s/g, '-').replace(/\s+/g, '-')}`}
        className={`relative group w-full flex items-center gap-2 px-3 ${D.groupHeader} rounded-[10px] font-extrabold uppercase tracking-[0.07em] transition-all ${
          hasActive
            ? 'text-primary bg-primary/10 ring-1 ring-primary/20 shadow-[0_1px_2px_rgba(0,0,0,0.04)]'
            : 'text-muted-foreground/85 hover:text-foreground hover:bg-white/60 dark:hover:bg-stone-700/30'
        }`}
      >
        {/* iter515 — strong active-group left rail; iter895 tone rail
            shows even when inactive so the color band remains a
            wayfinding cue. */}
        {hasActive
          ? <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full bg-primary" />
          : tone && <span className={`absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full ${tone.rail}`} />}
        <GroupIcon
          size={D.groupIcon}
          strokeWidth={hasActive ? 2.4 : 1.7}
          className={`flex-shrink-0 transition-colors ${hasActive ? 'text-primary' : 'opacity-60 group-hover:opacity-100'}`}
        />
        <span className={`flex-1 text-left truncate ${hasActive ? 'tracking-[0.05em] text-[12px]' : ''}`}>{t(navLabelToKey[group.label] || group.label)}</span>
        <ChevronDown
          size={12}
          className={`transition-transform duration-200 ${effectiveOpen ? 'rotate-180' : ''} ${hasActive ? 'opacity-80 text-primary' : 'opacity-50 group-hover:opacity-80'}`}
        />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-200 ease-out ${effectiveOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
        <div className="overflow-hidden">
          <div className={`${D.spacing} pb-1.5 pt-1 px-2`}>
            <TooltipProvider delayDuration={400}>
            {visibleItems.map((item) => {
              const desc = NAV_DESCRIPTIONS[item.path];
              const linkEl = (
                <NavItemLink
                  item={item}
                  currentPath={currentPath}
                  searchSelectedPath={searchSelectedPath}
                  onNavigate={onNavigate}
                  t={t}
                  flags={flags}
                  indent="pl-6"
                  density={density}
                />
              );
              return desc ? (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>{linkEl}</TooltipTrigger>
                  <TooltipContent side="right" className="max-w-[220px] text-xs" sideOffset={8}>
                    <p>{desc}</p>
                  </TooltipContent>
                </Tooltip>
              ) : <div key={item.path}>{linkEl}</div>;
            })}
            </TooltipProvider>
            {/* iter614 — Show-more toggle for compact mode. */}
            {(needsCompact || (showAll && filteredItems.length > COMPACT_LIMIT)) && !isSearching && (
              <button
                type="button"
                onClick={toggleShowAll}
                data-testid={`nav-show-more-${group.label.toLowerCase().replace(/\s&\s/g, '-').replace(/\s+/g, '-')}`}
                className="w-full mt-0.5 pl-6 pr-3 py-[6px] rounded-[10px] text-[11px] font-semibold text-primary/80 hover:bg-primary/5 transition-colors text-left"
              >
                {showAll ? '— Show less' : `+ Show all (${hiddenCount} more)`}
              </button>
            )}
            {/* iter930 — Nested sub-folders. Rendered AFTER the flat
                 top-level items so an operator sees the most common
                 destinations first, then the specialised sub-sections
                 below. Each sub-folder remembers its own open/closed
                 state via localStorage. */}
            {filteredSubgroups.length > 0 && (
              <div className="mt-1 pt-1 border-t border-border/30 space-y-0.5">
                {filteredSubgroups.map((sub) => (
                  <SubFolder
                    key={sub.label}
                    parentLabel={group.label}
                    sub={sub}
                    permittedItems={sub.permittedItems}
                    currentPath={currentPath}
                    searchSelectedPath={searchSelectedPath}
                    onNavigate={onNavigate}
                    t={t}
                    flags={flags}
                    isSearching={isSearching}
                    density={density}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// iter943 — Sidebar Portal Transformation. Each top-level department now
// renders as a SINGLE-CLICK link to its Hub page (big-tile landing) instead
// of a long collapsible dropdown. Reuses this file's GROUP_ICONS/GROUP_TONES
// so the sidebar visual language is unchanged — no counts, no chevron.
export const hubSlug = (label) =>
  String(label || '').toLowerCase().replace(/&/g, ' ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export function SidebarHubLink({ group, currentPath, onNavigate, t, colorsEnabled, density = 'comfortable', pendingOffsets = 0 }) {
  const D = DENSITY[density] || DENSITY.comfortable;
  const Icon = GROUP_ICONS[group.label] || Folder;
  const slug = hubSlug(group.label);
  const to = `/hub/${slug}`;
  // Active when we're on the hub itself OR on any page that belongs to it.
  const allPaths = [
    ...(group.items || []).map((i) => i.path),
    ...((group.subgroups || []).flatMap((s) => (s.items || []).map((i) => i.path))),
  ];
  const hasActive = currentPath === to || allPaths.includes(currentPath);
  // iter947 — show the amber pending-offsets badge on the department that owns
  // the Reconciliation Center (Finance & Banking).
  const ownsRecon = allPaths.includes('/reconciliation-center');
  const showOffsets = ownsRecon && pendingOffsets > 0;
  const _colorsOn = (colorsEnabled !== undefined) ? colorsEnabled : true;
  const tone = _colorsOn ? GROUP_TONES[group.label] : null;
  return (
    <div className={`mb-1 rounded-xl mx-1 my-0.5 transition-colors ${tone?.bg || ''}`}>
      <Link
        to={to}
        onClick={() => onNavigate?.(to)}
        data-testid={`nav-hub-${slug}`}
        className={`relative group w-full flex items-center gap-2 px-3 ${D.groupHeader} rounded-[10px] font-extrabold uppercase tracking-[0.07em] transition-all ${
          hasActive
            ? 'text-primary bg-primary/10 ring-1 ring-primary/20 shadow-[0_1px_2px_rgba(0,0,0,0.04)]'
            : 'text-muted-foreground/85 hover:text-foreground hover:bg-white/60 dark:hover:bg-stone-700/30'
        }`}
      >
        {hasActive
          ? <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full bg-primary" />
          : tone && <span className={`absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full ${tone.rail}`} />}
        <Icon
          size={D.groupIcon}
          strokeWidth={hasActive ? 2.4 : 1.7}
          className={`flex-shrink-0 transition-colors ${hasActive ? 'text-primary' : 'opacity-60 group-hover:opacity-100'}`}
        />
        <span className={`flex-1 text-left truncate ${hasActive ? 'tracking-[0.05em] text-[12px]' : ''}`}>{t(navLabelToKey[group.label] || group.label)}</span>
        {showOffsets && (
          <span
            className="shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300"
            data-testid="sidebar-pending-offsets-badge"
            title={`${pendingOffsets} pending cross-branch approvals`}
          >
            {pendingOffsets}
          </span>
        )}
        <ChevronRight size={12} className={`${hasActive ? 'opacity-80 text-primary' : 'opacity-40 group-hover:opacity-70'}`} />
      </Link>
    </div>
  );
}
